import { useState, useEffect } from 'react';

type SubscriptionPlanWithExtensions = {
  translations?: Record<string, unknown>;
};
import { Loader2, Plus, Trash2, Info, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  SubscriptionPlan, 
  MultiCurrencyPrice,
  formatPriceWithCycle
} from '@/types/billing';
import { 
  useCreateSubscriptionPlan, 
  useUpdateSubscriptionPlan,
  useBillingCycles 
} from '@/hooks/billing';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { 
  SubscriptionPlanCreateData,
  PlanPricingData,
  PlanCreditSettingsData
} from '@/services/billing/subscription-plans.service';
import { TranslationButton, TranslationDialog } from '@/components/dialogs/TranslationDialog';
import { FeaturesTranslationDialog } from '@/components/dialogs/FeaturesTranslationDialog';
import { useTranslation } from 'react-i18next';
// Translation components ready for use when needed
// import { TranslationInput } from '@/components/billing/translations/TranslationInput';
// import { TranslationFeaturesList } from '@/components/billing/translations/TranslationFeaturesList';
// import { Globe } from 'lucide-react';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type PlanPricingWithIntro = PlanPricingData & {
  introPrices?: MultiCurrencyPrice;
  recurringPrices?: MultiCurrencyPrice;
  introCycles?: number;
};

type SubscriptionPlanFormData = Omit<SubscriptionPlanCreateData, 'pricing'> & {
  pricing?: PlanPricingWithIntro[];
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'Fr',
  CNY: '¥',
  SEK: 'kr',
  NZD: 'NZ$',
  MXN: '$',
  SGD: 'S$',
  HKD: 'HK$',
  NOK: 'kr',
  KRW: '₩',
};

interface SubscriptionPlanEditorProps {
  productId: string;
  plan?: Partial<SubscriptionPlan>;
  isOpen: boolean;
  onClose: () => void;
  activeCurrencies?: string[];
  defaultCurrency?: string;
  supportedLanguages?: string[];
  currentLanguage?: string;
}

export function SubscriptionPlanEditor({
  productId,
  plan,
  isOpen,
  onClose,
  activeCurrencies = ['USD'],
  defaultCurrency = 'USD',
  supportedLanguages = ['en'],
  currentLanguage,
}: SubscriptionPlanEditorProps) {
  const { t, i18n } = useTranslation();
  const isEditing = !!plan?.id;
  const resolvedLanguage = currentLanguage ?? i18n.language ?? i18n.resolvedLanguage ?? 'en';
  const baseLanguage = resolvedLanguage.split('-')[0] ?? resolvedLanguage;
  const activeLanguage =
    supportedLanguages.includes(resolvedLanguage)
      ? resolvedLanguage
      : supportedLanguages.includes(baseLanguage)
        ? baseLanguage
        : supportedLanguages[0] ?? 'en';
  
  // Form state
  const [formData, setFormData] = useState<SubscriptionPlanFormData>({
    name: '',
    description: '',
    type: 'individual',
    isRecommended: false,
    isAvailableInCheckout: true,
    status: 'draft',
    freeTrialDays: 0,
    trialRequiresCard: false,
    prorateChanges: true,
    allowCancellation: true,
    includedSeats: undefined,
    pricing: [],
    creditSettings: {
      includeCredits: false,
      creditsPerCycle: 0,
      allowTopUp: false,
      topUpPricesPerCredit: {}
    },
    features: {
      features: [],
      featuresTranslations: undefined
    },
    featureFlags: {}
  });

  // Additional UI state
  const [newFeature, setNewFeature] = useState('');
  const [showTranslationDialog, setShowTranslationDialog] = useState(false);
  const [showFeaturesTranslationDialog, setShowFeaturesTranslationDialog] = useState(false);
  const [showDescriptionTranslationDialog, setShowDescriptionTranslationDialog] = useState(false);
  const [nameTranslations, setNameTranslations] = useState<{ [lang: string]: string }>({});
  const [descriptionTranslations, setDescriptionTranslations] = useState<{ [lang: string]: string }>({});
  const [featuresTranslations, setFeaturesTranslations] = useState<{ [lang: string]: string[] }>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [allowAdditionalSeats, setAllowAdditionalSeats] = useState(true);
  const [introEnabledByCycle, setIntroEnabledByCycle] = useState<Record<string, boolean>>({});
  // const [useAdvancedTranslations, setUseAdvancedTranslations] = useState(supportedLanguages.length > 1);

  // Debug: Log translations when dialog opens
  useEffect(() => {
    if (showTranslationDialog) {
      console.log('TranslationDialog opened - nameTranslations:', nameTranslations);
      console.log('TranslationDialog opened - supportedLanguages:', supportedLanguages);
    }
  }, [showTranslationDialog, nameTranslations, supportedLanguages]);

  // Fetch billing cycles
  const { data: billingCycles = [] } = useBillingCycles(productId);
  
  // Fetch available feature flags
  const { data: featureFlagsResponse } = useFeatureFlags(productId);
  const availableFeatureFlags = featureFlagsResponse?.data || [];
  
  // Mutations
  const createMutation = useCreateSubscriptionPlan();
  const updateMutation = useUpdateSubscriptionPlan();

  const formatCurrency = (value: number, currency: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);

  const normalizeBillingCycle = (cycle: { id: string; name?: string; displayName?: string; months: number }) => ({
    id: cycle.id,
    name: cycle.name ?? cycle.displayName ?? cycle.id,
    months: cycle.months,
    displayName: cycle.displayName ?? cycle.name ?? cycle.id,
  });

  const resolveCurrencySymbol = (currency: string) => CURRENCY_SYMBOLS[currency] || currency;

  // Initialize form data from plan prop
  useEffect(() => {
    if (plan) {
      console.log('SubscriptionPlanEditor - Received plan:', plan);
      console.log('SubscriptionPlanEditor - Plan translations:', plan.translations);
      console.log('SubscriptionPlanEditor - Plan featureFlags:', plan.featureFlags);
      
      // Convert prices array to pricing format expected by the form
      let pricing: PlanPricingWithIntro[] = [];
      
      // The deserializer converts 'prices' relationship to 'pricesArray' to avoid conflicts
      const pricesData = plan.pricesArray || plan.prices || [];
      console.log('SubscriptionPlanEditor - Loading prices data with', pricesData?.length, 'price records');
      
      if (pricesData && Array.isArray(pricesData)) {
        // Group prices by billing_cycle_id (already camelCase from deserializer)
        const pricingByCycle: { [cycleId: string]: PlanPricingWithIntro } = {};
        
        pricesData.forEach((price: Record<string, unknown>) => {
          const cycleId = (price.billingCycleId as string) || (price.billing_cycle_id as string) || '';
          const currency = (price.currency as string) || 'USD';
          const amount = (price.basePrice as number) ?? (price.base_price as number) ?? 0;
          const perSeatPrice = (price.perSeatPrice as number | null) ?? (price.per_seat_price as number | null);
          const introPrice = (price.introPrice as number | undefined) ?? (price.intro_price as number | undefined);
          const recurringPrice =
            (price.recurringPrice as number | undefined) ?? (price.recurring_price as number | undefined);
          const introCycles =
            (price.introCycles as number | undefined) ?? (price.intro_cycles as number | undefined);
          const priceId = (price.id as string) || '';
          
          if (!pricingByCycle[cycleId]) {
            pricingByCycle[cycleId] = {
              billingCycleId: cycleId,
              prices: {},
              priceIds: {},
              defaultCurrency: defaultCurrency || 'USD'
            };
          }
          
          pricingByCycle[cycleId].prices[currency] = amount;

          if (introPrice !== undefined) {
            if (!pricingByCycle[cycleId].introPrices) {
              pricingByCycle[cycleId].introPrices = {};
            }
            pricingByCycle[cycleId].introPrices![currency] = introPrice;
          }

          if (recurringPrice !== undefined) {
            if (!pricingByCycle[cycleId].recurringPrices) {
              pricingByCycle[cycleId].recurringPrices = {};
            }
            pricingByCycle[cycleId].recurringPrices![currency] = recurringPrice;
          }

          if (introCycles !== undefined && pricingByCycle[cycleId].introCycles === undefined) {
            pricingByCycle[cycleId].introCycles = introCycles;
          }
          
          // Store price ID for updating existing prices
          if (priceId) {
            if (!pricingByCycle[cycleId].priceIds) {
              pricingByCycle[cycleId].priceIds = {};
            }
            pricingByCycle[cycleId].priceIds![currency] = priceId;
          }
          
          // Add per-seat pricing for teams plans
          if (perSeatPrice !== null && perSeatPrice !== undefined) {
            if (!pricingByCycle[cycleId].pricesPerAdditionalSeat) {
              pricingByCycle[cycleId].pricesPerAdditionalSeat = {};
            }
            pricingByCycle[cycleId].pricesPerAdditionalSeat![currency] = perSeatPrice;
          }
        });
        
        pricing = Object.values(pricingByCycle);
        console.log('SubscriptionPlanEditor - Final pricing structure:', pricing);
      } else if (plan.pricing) {
        // Use existing pricing if available
        pricing = plan.pricing as PlanPricingWithIntro[];
      }

      // Handle trial days - deserializer converts to camelCase
      const trialDays = plan.trialDays || plan.freeTrialDays || plan.trial_days || 0;
      const trialRequiresCard =
        plan.trialRequiresCard ?? plan.trial_requires_card ?? false;
      
      // Handle credit settings - deserializer converts to camelCase
      let creditSettings: PlanCreditSettingsData;
      
      // The deserializer should have converted credit_settings to creditSettings
      const creditSettingsData = plan.creditSettings || plan.credit_settings;
      
      if (creditSettingsData) {
        // Type assertion to handle both camelCase and snake_case properties
        const settings = creditSettingsData as Record<string, unknown>;
        creditSettings = {
          includeCredits: (settings.includesCredits as boolean) ?? (settings.includes_credits as boolean) ?? (settings.includeCredits as boolean) ?? false,
          creditsPerCycle: (settings.creditsPerCycle as number) ?? (settings.credits_per_cycle as number) ?? 0,
          allowTopUp: (settings.allowPurchase as boolean) ?? (settings.allow_purchase as boolean) ?? (settings.allowTopUp as boolean) ?? false,
          topUpPricesPerCredit: (settings.topUpPricesPerCredit as Record<string, number>) ?? (settings.top_up_prices_per_credit as Record<string, number>) ?? {}
        };
      } else {
        // Default values
        creditSettings = {
          includeCredits: false,
          creditsPerCycle: 0,
          allowTopUp: false,
          topUpPricesPerCredit: {}
        };
      }

      // Features are already at the top level after deserialization
      const featuresData = plan.features || [];
      
      // Feature flags - the API should now include them directly in plan.featureFlags
      // after we added the include parameter to the getSubscriptionPlan request
      const featureFlagsData = plan.featureFlags || {};
      
      console.log('SubscriptionPlanEditor - Feature flags data found:', featureFlagsData);
      console.log('SubscriptionPlanEditor - Feature flags keys:', Object.keys(featureFlagsData));
      
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        type: plan.planType || plan.plan_type || plan.type || 'individual',
        isRecommended: plan.isRecommended ?? plan.is_recommended ?? false,
        isAvailableInCheckout:
          (plan as SubscriptionPlan & { isAvailableInCheckout?: boolean; is_available_in_checkout?: boolean })
            .isAvailableInCheckout ??
          (plan as SubscriptionPlan & { isAvailableInCheckout?: boolean; is_available_in_checkout?: boolean })
            .is_available_in_checkout ??
          true,
        status: plan.status || 'draft',
        freeTrialDays: trialDays,
        trialRequiresCard,
        prorateChanges: plan.prorateChanges ?? plan.prorate_changes ?? true,
        allowCancellation: plan.allowCancellation ?? plan.allow_cancellation ?? true,
        includedSeats: plan.includedSeats || plan.included_seats || 1,
        pricing,
        creditSettings,
        features: {
          features: Array.isArray(featuresData) ? featuresData : [],
          featuresTranslations: plan.featuresTranslations
        },
        featureFlags: featureFlagsData
      });
      const introEnabled: Record<string, boolean> = {};
      pricing.forEach((entry) => {
        const cycleId = entry.billingCycleId;
        if (!cycleId) return;
        const hasIntroPrices = Object.values(entry.introPrices ?? {}).some((value) => (value ?? 0) > 0);
        introEnabled[cycleId] = hasIntroPrices;
      });
      setIntroEnabledByCycle(introEnabled);
      // Handle translations from API format
      const planWithExt = plan as unknown as SubscriptionPlanWithExtensions;
      if (planWithExt.translations) {
        const trans = planWithExt.translations;
        console.log('SubscriptionPlanEditor - Translations from API:', trans);
        // API returns displayName (camelCase after deserialization) for name translations
        const nameTransData = (trans?.displayName as Record<string, string>) || (trans?.display_name as Record<string, string>) || (trans?.name as Record<string, string>) || plan.nameTranslations || {};
        console.log('SubscriptionPlanEditor - Name translations:', nameTransData);
        setNameTranslations(nameTransData as { [lang: string]: string });
        setDescriptionTranslations((trans?.description as Record<string, string>) || {});
        
        // Convert features array format to indexed format
        if (trans?.features && Array.isArray(trans.features)) {
          const indexedFeatures: { [lang: string]: string[] } = {};
          trans.features.forEach((featureTrans: Record<string, string>, index: number) => {
            Object.keys(featureTrans).forEach(lang => {
              if (!indexedFeatures[lang]) {
                indexedFeatures[lang] = [];
              }
              indexedFeatures[lang][index] = featureTrans[lang];
            });
          });
          setFeaturesTranslations(indexedFeatures as { [lang: string]: string[] });
        } else {
          setFeaturesTranslations(plan.featuresTranslations || {});
        }
      } else {
        // Fallback for old format or when translations are not in expected structure
        setNameTranslations(plan.nameTranslations || {});
        setDescriptionTranslations(plan.descriptionTranslations || {});
        setFeaturesTranslations(plan.featuresTranslations || {});
      }
      
      // Set allowAdditionalSeats based on whether any pricing has additional seat prices
      const hasAdditionalSeatPricing = pricing.some(p => 
        p.pricesPerAdditionalSeat && Object.keys(p.pricesPerAdditionalSeat).length > 0
      );
      setAllowAdditionalSeats(hasAdditionalSeatPricing);
    }
  }, [plan, defaultCurrency]);

  useEffect(() => {
    if (!isOpen || plan) return;
    if (Object.keys(introEnabledByCycle).length > 0) return;
    const initialIntroState: Record<string, boolean> = {};
    billingCycles.forEach((cycle) => {
      initialIntroState[cycle.id] = false;
    });
    if (billingCycles.length > 0) {
      setIntroEnabledByCycle(initialIntroState);
    }
  }, [billingCycles, introEnabledByCycle, isOpen, plan]);

  // Monitor formData changes for debugging
  useEffect(() => {
    if (formData.features?.features?.length) {
      console.log('Features updated:', formData.features.features);
    }
  }, [formData.features]);

  const handleAddFeature = () => {
    console.log('handleAddFeature called - newFeature:', newFeature);
    console.log('handleAddFeature - current formData.features:', formData.features);
    
    if (newFeature.trim()) {
      const currentFeatures = formData.features?.features || [];
      const updatedFeatures = [...currentFeatures, newFeature.trim()];
      console.log('handleAddFeature - Current features:', currentFeatures);
      console.log('handleAddFeature - Updated features:', updatedFeatures);
      
      // Update formData with new features
      const updatedFormData = {
        ...formData,
        features: {
          features: updatedFeatures,
          featuresTranslations: formData.features?.featuresTranslations
        }
      };
      console.log('handleAddFeature - Updated formData:', updatedFormData);
      console.log('handleAddFeature - Updated formData.features.features:', updatedFormData.features.features);
      
      setFormData(updatedFormData);
      setNewFeature('');
    } else {
      console.log('handleAddFeature - newFeature is empty, not adding');
    }
  };

  const handleRemoveFeature = (index: number) => {
    const currentFeatures = formData.features?.features || [];
    const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
    // Update formData with removed feature
    setFormData({
      ...formData,
      features: {
        features: updatedFeatures,
        featuresTranslations: formData.features?.featuresTranslations
      }
    });
  };

  const handlePricingChange = (cycleId: string, prices: MultiCurrencyPrice) => {
    console.log('handlePricingChange - cycleId:', cycleId, 'prices:', prices);
    const existingPricing = formData.pricing || [];
    const updatedPricing = existingPricing.map(p => 
      p.billingCycleId === cycleId 
        ? { ...p, prices } // Preserve existing priceIds and other properties
        : p
    );
    
    // Add new pricing if not exists
    if (!updatedPricing.find(p => p.billingCycleId === cycleId)) {
      updatedPricing.push({
        billingCycleId: cycleId,
        prices,
        defaultCurrency
      });
    }
    
    // Clear validation errors for this cycle when price is updated
    const newErrors = { ...validationErrors };
    Object.keys(prices).forEach(currency => {
      const errorKey = `${cycleId}-${currency}`;
      if (prices[currency] && prices[currency] > 0) {
        delete newErrors[errorKey];
      }
    });
    setValidationErrors(newErrors);
    
    console.log('handlePricingChange - updatedPricing:', updatedPricing);
    setFormData({ ...formData, pricing: updatedPricing });
  };

  const handleIntroToggle = (cycleId: string, enabled: boolean) => {
    setIntroEnabledByCycle((prev) => {
      const next = { ...prev, [cycleId]: enabled };
      if (!enabled) {
        delete next[cycleId];
      }
      return next;
    });
    setFormData((prev) => {
      const existingPricing = prev.pricing || [];
      const hasEntry = existingPricing.some((entry) => entry.billingCycleId === cycleId);
      const basePrices: MultiCurrencyPrice = {};
      activeCurrencies.forEach((currency) => {
        basePrices[currency] = 0;
      });

      const updatedPricing = hasEntry
        ? existingPricing.map((entry) =>
            entry.billingCycleId === cycleId
              ? {
                  ...entry,
                  introPrices: enabled ? entry.introPrices ?? { ...basePrices } : { ...basePrices },
                  introCycles: enabled ? entry.introCycles ?? 1 : 0,
                  recurringPrices: enabled ? entry.recurringPrices : undefined,
                }
              : entry
          )
        : [
            ...existingPricing,
            {
              billingCycleId: cycleId,
              prices: { ...basePrices },
              introPrices: enabled ? { ...basePrices } : { ...basePrices },
              introCycles: enabled ? 1 : 0,
              defaultCurrency,
            },
          ];

      if (!enabled) {
        setValidationErrors((current) => {
          const nextErrors = { ...current };
          Object.keys(nextErrors).forEach((key) => {
            if (key.startsWith(`intro-${cycleId}-`) || key === `intro-cycles-${cycleId}`) {
              delete nextErrors[key];
            }
          });
          return nextErrors;
        });
      }

      return { ...prev, pricing: updatedPricing };
    });
  };

  const handleIntroPriceChange = (cycleId: string, currency: string, value: string) => {
    const numericPrice = parseFloat(value) || 0;
    const existingPricing = formData.pricing || [];
    const basePrices: MultiCurrencyPrice = {};
    activeCurrencies.forEach((code) => {
      basePrices[code] = 0;
    });

    let updatedPricing = existingPricing.map((entry) => {
      if (entry.billingCycleId !== cycleId) return entry;
      return {
        ...entry,
        introPrices: {
          ...(entry.introPrices ?? basePrices),
          [currency]: numericPrice,
        },
      };
    });

    if (!updatedPricing.find((entry) => entry.billingCycleId === cycleId)) {
      updatedPricing = [
        ...updatedPricing,
        {
          billingCycleId: cycleId,
          prices: { ...basePrices },
          introPrices: { ...basePrices, [currency]: numericPrice },
          introCycles: 1,
          defaultCurrency,
        },
      ];
    }

    const errorKey = `intro-${cycleId}-${currency}`;
    if (numericPrice > 0 && validationErrors[errorKey]) {
      const nextErrors = { ...validationErrors };
      delete nextErrors[errorKey];
      setValidationErrors(nextErrors);
    }

    setFormData({ ...formData, pricing: updatedPricing });
  };

  const handleIntroCyclesChange = (cycleId: string, value: string) => {
    const numericValue = parseInt(value, 10) || 0;
    const existingPricing = formData.pricing || [];
    const updatedPricing = existingPricing.map((entry) =>
      entry.billingCycleId === cycleId ? { ...entry, introCycles: numericValue } : entry
    );

    const errorKey = `intro-cycles-${cycleId}`;
    if (numericValue >= 1 && validationErrors[errorKey]) {
      const nextErrors = { ...validationErrors };
      delete nextErrors[errorKey];
      setValidationErrors(nextErrors);
    }

    setFormData({ ...formData, pricing: updatedPricing });
  };

  const validatePricing = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    // Check each billing cycle
    billingCycles.forEach(cycle => {
      const cyclePrice = formData.pricing?.find(p => p.billingCycleId === cycle.id);
      const introEnabled = introEnabledByCycle[cycle.id];
      
      // Check each currency
      activeCurrencies.forEach(currency => {
        const price = cyclePrice?.prices?.[currency];
        const errorKey = `${cycle.id}-${currency}`;
        
        if (!price || price <= 0) {
          errors[errorKey] = t('products.subscriptionPlanEditor.validation.priceForCurrency', { currency });
          isValid = false;
        }
      });

      if (introEnabled) {
        const introCycles = cyclePrice?.introCycles ?? 0;
        if (introCycles < 1) {
          errors[`intro-cycles-${cycle.id}`] = t('products.subscriptionPlanEditor.validation.introCyclesMin');
          isValid = false;
        }

        activeCurrencies.forEach(currency => {
          const introPrice = cyclePrice?.introPrices?.[currency];
          const errorKey = `intro-${cycle.id}-${currency}`;
          if (!introPrice || introPrice <= 0) {
            errors[errorKey] = t('products.subscriptionPlanEditor.validation.introPriceForCurrency', { currency });
            isValid = false;
          }
        });
      }
    });

    // If credit top-up is enabled, validate top-up prices
    if (formData.creditSettings?.allowTopUp) {
      activeCurrencies.forEach(currency => {
        const topUpPrice = formData.creditSettings?.topUpPricesPerCredit?.[currency];
        const errorKey = `topup-${currency}`;
        
        if (!topUpPrice || topUpPrice <= 0) {
          errors[errorKey] = t('products.subscriptionPlanEditor.validation.topUpPriceForCurrency', { currency });
          isValid = false;
        }
      });
    }

    setValidationErrors(errors);
    return isValid;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Validate name is not empty
    if (!formData.name || formData.name.trim().length === 0) {
      errors['name'] = t('products.subscriptionPlanEditor.validation.nameRequired');
      setValidationErrors(errors);
      return false;
    }

    // Validate name length (slug will be generated from name)
    if (formData.name.trim().length < 3) {
      errors['name'] = t('products.subscriptionPlanEditor.validation.nameMin');
      setValidationErrors(errors);
      return false;
    }

    if (formData.name.trim().length > 100) {
      errors['name'] = t('products.subscriptionPlanEditor.validation.nameMax');
      setValidationErrors(errors);
      return false;
    }
    
    // Clear name error if valid
    setValidationErrors({});
    return true;
  };

  const handleSave = async () => {
    // Validate form first
    if (!validateForm()) {
      toast.error(t('products.subscriptionPlanEditor.toasts.fixValidation'));
      return;
    }

    // Validate pricing
    if (!validatePricing()) {
      toast.error(t('products.subscriptionPlanEditor.pricingWarning'));
      return;
    }
    
    // Prepare data for submission
    const submitData: Record<string, unknown> & { translations?: { name?: Record<string, string>; description?: Record<string, string>; features?: Array<Record<string, string>> } } = { ...formData };
    if ((submitData.freeTrialDays as number | undefined) === undefined || (submitData.freeTrialDays as number) <= 0) {
      delete submitData.trialRequiresCard;
    }
    
    // Ensure pricing data includes price IDs for updates
    if (submitData.pricing && Array.isArray(submitData.pricing)) {
      submitData.pricing = submitData.pricing.map((pricing: PlanPricingWithIntro) => {
        const hasIntroPrices = Object.values(pricing.introPrices ?? {}).some((value) => (value ?? 0) > 0);
        const introEnabled = introEnabledByCycle[pricing.billingCycleId] === true;
        const shouldIncludeIntro = introEnabled && hasIntroPrices;

        const nextPricing: PlanPricingWithIntro = {
          ...pricing,
          // Include price IDs if they exist (for updating existing prices)
          priceIds: pricing.priceIds || {},
        };

        if (shouldIncludeIntro) {
          nextPricing.recurringPrices = pricing.recurringPrices ?? pricing.prices;
          nextPricing.introCycles = pricing.introCycles ?? 1;
        } else {
          nextPricing.introPrices = pricing.introPrices ?? {};
          nextPricing.introCycles = 0;
          nextPricing.recurringPrices = undefined;
        }

        return nextPricing;
      });
    }
    
    // Format translations for API
    const hasTranslations = Object.keys(nameTranslations).length > 0 || 
                           Object.keys(descriptionTranslations).length > 0 || 
                           Object.keys(featuresTranslations).length > 0;
    
    if (hasTranslations) {
      submitData.translations = {
        // API expects 'name' field for name translations (will be stored as display_name)
        name: Object.keys(nameTranslations).length > 0 ? nameTranslations : undefined,
        description: Object.keys(descriptionTranslations).length > 0 ? descriptionTranslations : undefined,
        features: undefined
      };

      // Convert indexed features format to array format for API
      if (Object.keys(featuresTranslations).length > 0 && formData.features?.features) {
        const featuresArray: Record<string, string>[] = [];
        formData.features.features.forEach((_, index) => {
          const featureTrans: Record<string, string> = {};
          Object.keys(featuresTranslations).forEach(lang => {
            if (featuresTranslations[lang] && featuresTranslations[lang][index]) {
              featureTrans[lang] = featuresTranslations[lang][index];
            }
          });
          if (Object.keys(featureTrans).length > 0) {
            featuresArray.push(featureTrans);
          }
        });
        if (featuresArray.length > 0) {
          submitData.translations.features = featuresArray;
        }
      }
      
      // Clean up undefined fields
      if (!submitData.translations?.name) delete submitData.translations?.name;
      if (!submitData.translations?.description) delete submitData.translations?.description;
      if (!submitData.translations?.features) delete submitData.translations?.features;
      
      // If no translations remain, remove the translations object
      if (submitData.translations && Object.keys(submitData.translations).length === 0) delete submitData.translations;
    }
    
    // Only update features if they exist
    if (submitData.features && formData.features?.features) {
      submitData.features = {
        features: formData.features.features,
        featuresTranslations: undefined // Remove old format
      };
    }

    // Include feature flags - always include them even if empty object
    // The backend expects featureFlags to be present
    submitData.featureFlags = formData.featureFlags || {};

    console.log('SubscriptionPlanEditor - Form data before submission:', formData);
    console.log('SubscriptionPlanEditor - Submit data:', submitData);
    console.log('SubscriptionPlanEditor - Pricing data in submitData:', submitData.pricing);
    console.log('SubscriptionPlanEditor - Price IDs for each billing cycle:');
    (submitData.pricing as Array<{ billingCycleId: string; priceIds?: Record<string, string> }> | undefined)?.forEach((pricing) => {
      console.log(`  Cycle ${pricing.billingCycleId}:`, pricing.priceIds);
    });
    console.log('SubscriptionPlanEditor - Features from formData:', formData.features?.features);
    console.log('SubscriptionPlanEditor - Features in submitData:', (submitData as { features?: { features?: string[] } }).features?.features);
    console.log('SubscriptionPlanEditor - Feature flags from formData:', formData.featureFlags);
    console.log('SubscriptionPlanEditor - Feature flags in submitData:', submitData.featureFlags);
    console.log('SubscriptionPlanEditor - Feature flags keys:', Object.keys(formData.featureFlags || {}));
    console.log('SubscriptionPlanEditor - Features translations:', featuresTranslations);

    try {
      if (isEditing && plan?.id) {
        console.log('SubscriptionPlanEditor - Updating plan with ID:', plan.id);
        await updateMutation.mutateAsync({
          productId,
          planId: plan.id,
          data: submitData as unknown as SubscriptionPlanCreateData
        });
      toast.success(t('products.subscriptionPlanEditor.toasts.updateSuccess'));
      } else {
        console.log('SubscriptionPlanEditor - Creating new plan');
        await createMutation.mutateAsync({
          productId,
          data: submitData as unknown as SubscriptionPlanCreateData
        });
      toast.success(t('products.subscriptionPlanEditor.toasts.createSuccess'));
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving subscription plan:', error);
      
      let errorMessage = t('products.subscriptionPlanEditor.toasts.saveError');
      
      // Handle different error formats
      if (error && typeof error === 'object') {
        // Check for axios/fetch error response
        if ('response' in error && error.response && typeof error.response === 'object' && 'data' in error.response) {
          const responseData = error.response.data as { error?: string; message?: string };
          
          // Check for error field in response
          if (responseData.error && typeof responseData.error === 'string') {
            errorMessage = responseData.error;
          } else if (responseData.message) {
            errorMessage = responseData.message;
          }
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      }
      
      // Check for duplicate slug error
      if (errorMessage.includes('duplicate key') || errorMessage.includes('already exists') || errorMessage.includes('slug_unique')) {
        const nameError = t('products.subscriptionPlanEditor.toasts.duplicateName');
        setValidationErrors({ ...validationErrors, name: nameError });
        toast.error(nameError);
      } else {
        toast.error(t('products.subscriptionPlanEditor.toasts.saveError'));
      }
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {formData.type === 'teams'
              ? t('products.subscriptionPlanEditor.dialogTitleTeam')
              : t('products.subscriptionPlanEditor.dialogTitleIndividual')}
          </DialogTitle>
          <DialogDescription>
            {formData.type === 'teams'
              ? t('products.subscriptionPlanEditor.dialogDescriptionTeam')
              : t('products.subscriptionPlanEditor.dialogDescriptionIndividual')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="name">{t('products.subscriptionPlanEditor.nameLabel')}</Label>
                  <TranslationButton
                    onClick={() => setShowTranslationDialog(true)}
                    translationStatus={
                      Object.keys(nameTranslations).length > 0 ? 'complete' : 'none'
                    }
                  />
                </div>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const nextName = e.target.value;
                    console.log('Name input changed:', nextName);
                    setFormData({ ...formData, name: nextName });
                    setNameTranslations((prev) => {
                      if (supportedLanguages.length <= 1 && Object.keys(prev).length === 0) {
                        return prev;
                      }
                      if (prev[activeLanguage] === nextName) {
                        return prev;
                      }
                      return { ...prev, [activeLanguage]: nextName };
                    });
                    // Clear name validation error when user starts typing
                    if (validationErrors['name']) {
                      const newErrors = { ...validationErrors };
                      delete newErrors['name'];
                      setValidationErrors(newErrors);
                    }
                  }}
                  placeholder={t('products.subscriptionPlanEditor.namePlaceholder')}
                  className={validationErrors['name'] ? 'border-destructive' : ''}
                />
                {validationErrors['name'] && (
                  <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors['name']}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="description">{t('products.subscriptionPlanEditor.descriptionLabel')}</Label>
                  <TranslationButton
                    onClick={() => setShowDescriptionTranslationDialog(true)}
                    translationStatus={
                      Object.keys(descriptionTranslations).length > 0 ? 'complete' : 'none'
                    }
                  />
                </div>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => {
                    console.log('Description input changed:', e.target.value);
                    setFormData({ ...formData, description: e.target.value });
                  }}
                  placeholder={t('products.subscriptionPlanEditor.descriptionPlaceholder')}
                  rows={1}
                  className="min-h-[38px]"
                />
              </div>
            </div>

          </div>


          {/* Pricing by Billing Cycle */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">{t('products.subscriptionPlanEditor.pricingByCycleTitle')}</h3>
            
            {/* Validation Warning */}
            {Object.keys(validationErrors).length > 0 ? (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">{t('products.subscriptionPlanEditor.pricingWarning')}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-background border rounded-lg">
                <Info className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{t('products.subscriptionPlanEditor.pricingInfo')}</span>
              </div>
            )}

            {/* Pricing for each billing cycle */}
            {billingCycles.map((cycle) => {
              const cyclePrice = formData.pricing?.find(p => {
                return p.billingCycleId === cycle.id;
              });
              const introEnabled = introEnabledByCycle[cycle.id] ?? false;
              const summaryCurrency =
                (defaultCurrency && activeCurrencies.includes(defaultCurrency)
                  ? defaultCurrency
                  : activeCurrencies[0]) || 'USD';
              const summaryRecurringPrice = cyclePrice?.prices?.[summaryCurrency] ?? 0;
              const summaryIntroPrice = cyclePrice?.introPrices?.[summaryCurrency] ?? 0;
              const summaryIntroCycles = cyclePrice?.introCycles ?? 0;
              
              return (
                <div key={cycle.id} className="p-4 border rounded-lg space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="font-medium">{cycle.name}</h4>
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`intro-toggle-${cycle.id}`}
                        checked={introEnabled}
                        onCheckedChange={(checked) => handleIntroToggle(cycle.id, checked)}
                      />
                      <Label htmlFor={`intro-toggle-${cycle.id}`} className="text-sm font-normal cursor-pointer">
                        {t('products.subscriptionPlanEditor.introToggleLabel')}
                      </Label>
                    </div>
                  </div>
                  
                  {activeCurrencies.map((currency) => {
                    const price = cyclePrice?.prices?.[currency] || 0;
                    const monthlyEquivalent = cycle.months > 0 ? (price / cycle.months).toFixed(2) : '0.00';
                    
                    return (
                      <div key={currency} className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-sm">{currency}</Label>
                      {currency === defaultCurrency && (
                        <Badge variant="secondary" className="text-xs">
                          {t('products.defaultBadgeLabel')}
                        </Badge>
                      )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {resolveCurrencySymbol(currency)}
                              </span>
                              <Input
                                type="number"
                                step="0.01"
                                value={price}
                                onChange={(e) => {
                                  const newPrice = parseFloat(e.target.value) || 0;
                                  const updatedPrices = {
                                    ...(cyclePrice?.prices || {}),
                                    [currency]: newPrice
                                  };
                                  handlePricingChange(cycle.id, updatedPrices);
                                }}
                                placeholder={t('products.subscriptionPlanEditor.pricePlaceholder')}
                                className={validationErrors[`${cycle.id}-${currency}`] ? 'border-destructive' : ''}
                              />
                            </div>
                            {validationErrors[`${cycle.id}-${currency}`] && (
                              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {validationErrors[`${cycle.id}-${currency}`]}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {t('products.subscriptionPlanEditor.recurringPriceHelper')}
                            </p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-sm">{t('products.subscriptionPlanEditor.monthlyEquivalentLabel')}</Label>
                          <div className="px-3 py-2 bg-muted rounded-md text-sm">
                            {t('products.subscriptionPlanEditor.monthlyEquivalentValue', {
                              price: `${currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : ''}${monthlyEquivalent}`,
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {introEnabled && (
                    <div className="rounded-lg border border-muted bg-muted/20 p-4 space-y-4">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {t('products.subscriptionPlanEditor.introSectionTitle')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('products.subscriptionPlanEditor.introPriceHelper')}
                        </p>
                      </div>

                      <div className="grid gap-4">
                        {activeCurrencies.map((currency) => {
                          const introPrice = cyclePrice?.introPrices?.[currency] || 0;
                          const recurringPrice = cyclePrice?.prices?.[currency] || 0;
                          const showHigherWarning =
                            introPrice > 0 && recurringPrice > 0 && introPrice > recurringPrice;

                          return (
                            <div key={currency} className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Label className="text-sm">{currency}</Label>
                                  {currency === defaultCurrency && (
                                    <Badge variant="secondary" className="text-xs">
                                      {t('products.defaultBadgeLabel')}
                                    </Badge>
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">
                                      {resolveCurrencySymbol(currency)}
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={introPrice}
                                      onChange={(e) =>
                                        handleIntroPriceChange(cycle.id, currency, e.target.value)
                                      }
                                      placeholder={t('products.subscriptionPlanEditor.introPricePlaceholder')}
                                      className={
                                        validationErrors[`intro-${cycle.id}-${currency}`]
                                          ? 'border-destructive'
                                          : ''
                                      }
                                    />
                                  </div>
                                  {validationErrors[`intro-${cycle.id}-${currency}`] && (
                                    <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                                      <AlertCircle className="h-3 w-3" />
                                      {validationErrors[`intro-${cycle.id}-${currency}`]}
                                    </p>
                                  )}
                                  {showHigherWarning && (
                                    <p className="text-xs text-amber-600 mt-1">
                                      {t('products.subscriptionPlanEditor.introHigherWarning')}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div />
                            </div>
                          );
                        })}

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor={`intro-cycles-${cycle.id}`} className="text-sm">
                              {t('products.subscriptionPlanEditor.introCyclesLabel')}
                            </Label>
                            <Input
                              id={`intro-cycles-${cycle.id}`}
                              type="number"
                              min="1"
                              value={cyclePrice?.introCycles ?? 1}
                              onChange={(e) => handleIntroCyclesChange(cycle.id, e.target.value)}
                              className={
                                validationErrors[`intro-cycles-${cycle.id}`] ? 'border-destructive' : ''
                              }
                            />
                            {validationErrors[`intro-cycles-${cycle.id}`] && (
                              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                {validationErrors[`intro-cycles-${cycle.id}`]}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {t('products.subscriptionPlanEditor.introCyclesHelper')}
                            </p>
                          </div>
                          <div className="space-y-2">
                            {summaryIntroPrice > 0 && summaryRecurringPrice > 0 && summaryIntroCycles > 0 ? (
                                  <div className="rounded-md border border-muted bg-background px-3 py-2 text-sm text-muted-foreground">
                                {t('products.subscriptionPlanEditor.introSummary', {
                                  introPrice: formatCurrency(summaryIntroPrice, summaryCurrency),
                                  cycles: summaryIntroCycles,
                                  recurringPrice: formatPriceWithCycle(
                                    summaryRecurringPrice,
                                    normalizeBillingCycle(cycle),
                                    summaryCurrency
                                  ),
                                })}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                </div>
              );
            })}
          </div>

          {/* Team Settings - Only show for team plans */}
          {formData.type === 'teams' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('products.subscriptionPlanEditor.teamSettingsTitle')}</CardTitle>
                <CardDescription>{t('products.subscriptionPlanEditor.teamSettingsDescription')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="includedSeats">{t('products.subscriptionPlanEditor.includedSeatsLabel')}</Label>
                  <Input
                    id="includedSeats"
                    type="number"
                    min="1"
                    value={formData.includedSeats || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      includedSeats: parseInt(e.target.value) || undefined 
                    })}
                    placeholder={t('products.subscriptionPlanEditor.includedSeatsPlaceholder')}
                  />
                  <p className="text-sm text-muted-foreground">
                    {t('products.subscriptionPlanEditor.includedSeatsHelper')}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="allowAdditionalSeats"
                      checked={allowAdditionalSeats}
                      onCheckedChange={(checked) => {
                        setAllowAdditionalSeats(checked);
                        if (!checked) {
                          // Clear additional seat pricing when disabled
                          const updatedPricing = (formData.pricing || []).map(p => ({
                            ...p,
                            pricesPerAdditionalSeat: undefined
                          }));
                          setFormData({ ...formData, pricing: updatedPricing });
                        }
                      }}
                    />
                    <Label htmlFor="allowAdditionalSeats" className="text-base font-normal cursor-pointer">
                      {t('products.subscriptionPlanEditor.allowAdditionalSeatsLabel')}
                    </Label>
                  </div>

                  {allowAdditionalSeats && (
                    <div className="space-y-4 ml-6">
                      <div className="space-y-2">
                        <Label>{t('products.subscriptionPlanEditor.pricePerSeatLabel')}</Label>
                        <p className="text-sm text-muted-foreground">
                          {t('products.subscriptionPlanEditor.pricePerSeatDescription')}
                        </p>
                      </div>
                      
                      {activeCurrencies.map((currency) => {
                        // Get the first billing cycle's additional seat price as a reference
                        const firstCycle = billingCycles[0];
                        const cyclePrice = formData.pricing?.find(p => p.billingCycleId === firstCycle?.id);
                        const seatPrice = cyclePrice?.pricesPerAdditionalSeat?.[currency] || 0;
                        
                        return (
                          <div key={currency} className="grid grid-cols-[100px_1fr] gap-4 items-center">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">{currency}</Label>
                              {currency === defaultCurrency && (
                                <Badge variant="secondary" className="text-xs">
                                  {t('products.defaultBadgeLabel')}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : ''}
                              </span>
                              <Input
                                type="number"
                                step="0.01"
                                value={seatPrice}
                                onChange={(e) => {
                                  const newPrice = parseFloat(e.target.value) || 0;
                                  // Update all billing cycles with the same additional seat price
                                  const updatedPricing = billingCycles.map(cycle => {
                                    const existingPrice = formData.pricing?.find(p => p.billingCycleId === cycle.id);
                                    return {
                                      billingCycleId: cycle.id,
                                      prices: existingPrice?.prices || {},
                                      pricesPerAdditionalSeat: {
                                        ...(existingPrice?.pricesPerAdditionalSeat || {}),
                                        [currency]: newPrice
                                      },
                                      defaultCurrency
                                    };
                                  });
                                  setFormData({ ...formData, pricing: updatedPricing });
                                }}
                                placeholder={t('products.subscriptionPlanEditor.pricePlaceholder')}
                                className="flex-1"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Credits */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="includeCredits"
                  checked={formData.creditSettings?.includeCredits || false}
                  onCheckedChange={(checked) => 
                    setFormData({ 
                      ...formData, 
                      creditSettings: {
                        ...formData.creditSettings!,
                        includeCredits: checked
                      }
                    })
                  }
                />
                <Label htmlFor="includeCredits" className="text-base font-normal cursor-pointer">
                  {t('products.subscriptionPlanEditor.includeCreditsLabel')}
                </Label>
              </div>

              {formData.creditSettings?.includeCredits && (
                <>
                  <div className="flex items-center gap-2 p-3 bg-background border rounded-lg">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{t('products.subscriptionPlanEditor.creditsInfo')}</span>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="creditsPerCycle">{t('products.subscriptionPlanEditor.creditsPerCycleLabel')}</Label>
                    <Input
                      id="creditsPerCycle"
                      type="number"
                      min="0"
                      value={formData.creditSettings?.creditsPerCycle || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        creditSettings: {
                          ...(formData.creditSettings || { includeCredits: false, allowTopUp: false }),
                          creditsPerCycle: parseInt(e.target.value) || 0
                        }
                      })}
                      placeholder={t('products.subscriptionPlanEditor.creditsPerCyclePlaceholder')}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Switch
                        id="allowTopUp"
                        checked={formData.creditSettings?.allowTopUp || false}
                        onCheckedChange={(checked) => 
                          setFormData({ 
                            ...formData, 
                            creditSettings: {
                              ...(formData.creditSettings || { includeCredits: false, creditsPerCycle: 0 }),
                              allowTopUp: checked
                            }
                          })
                        }
                      />
                    <Label htmlFor="allowTopUp" className="text-base font-normal cursor-pointer">
                      {t('products.subscriptionPlanEditor.allowTopUpLabel')}
                    </Label>
                    </div>

                    {formData.creditSettings?.allowTopUp && (
                      <div className="p-4 border rounded-lg space-y-4">
                        <div className="space-y-1">
                        <Label>{t('products.subscriptionPlanEditor.topUpPriceLabel')}</Label>
                        <p className="text-sm text-muted-foreground">{t('products.subscriptionPlanEditor.topUpPriceDescription')}</p>
                        </div>
                        
                        <div className="space-y-3">
                          {activeCurrencies.map((currency) => {
                            const price = formData.creditSettings?.topUpPricesPerCredit?.[currency] || 0;
                            
                            return (
                              <div key={currency} className="grid grid-cols-[100px_1fr] gap-4 items-center">
                                <div className="flex items-center gap-2">
                                  <Label className="text-sm">{currency}</Label>
                                  {currency === defaultCurrency && (
                                    <Badge variant="secondary" className="text-xs">
                                      {t('products.defaultBadgeLabel')}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">
                                      {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : ''}
                                    </span>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={price}
                                      onChange={(e) => {
                                        const newPrice = parseFloat(e.target.value) || 0;
                                        setFormData({ 
                                          ...formData, 
                                          creditSettings: {
                                            ...(formData.creditSettings || { includeCredits: false, creditsPerCycle: 0, allowTopUp: false }),
                                            topUpPricesPerCredit: {
                                              ...(formData.creditSettings?.topUpPricesPerCredit || {}),
                                              [currency]: newPrice
                                            }
                                          }
                                        });
                                        
                                        // Clear validation error for this currency if price is valid
                                        if (newPrice > 0) {
                                          const newErrors = { ...validationErrors };
                                          delete newErrors[`topup-${currency}`];
                                          setValidationErrors(newErrors);
                                        }
                                      }}
                                      placeholder={t('products.subscriptionPlanEditor.pricePlaceholder')}
                                      className={validationErrors[`topup-${currency}`] ? 'border-destructive flex-1' : 'flex-1'}
                                    />
                                  </div>
                                  {validationErrors[`topup-${currency}`] && (
                                    <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                                      <AlertCircle className="h-3 w-3" />
                                      {validationErrors[`topup-${currency}`]}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Features */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-medium mb-1">{t('products.subscriptionPlanEditor.featuresTitle')}</h3>
                <p className="text-sm text-muted-foreground">{t('products.subscriptionPlanEditor.featuresDescription')}</p>
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={newFeature}
                  onChange={(e) => {
                    console.log('Feature input changed:', e.target.value);
                    setNewFeature(e.target.value);
                  }}
                  placeholder={t('products.subscriptionPlanEditor.featurePlaceholder')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      console.log('Enter key pressed in feature input');
                      handleAddFeature();
                    }
                  }}
                  className="flex-1"
                />
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Add Feature button clicked');
                      handleAddFeature();
                    }}
                    size="default"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('common.add')}
                  </Button>
                  {(formData.features?.features?.length || 0) > 0 && (
                    <TranslationButton
                      onClick={() => setShowFeaturesTranslationDialog(true)}
                      translationStatus={
                        Object.keys(featuresTranslations).length > 0 ? 'complete' : 'none'
                      }
                    />
                  )}
                </div>
              </div>

              {(formData.features?.features?.length || 0) > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {t('products.subscriptionPlanEditor.featuresCount', {
                      count: formData.features?.features?.length || 0,
                    })}
                  </p>
                  {(formData.features?.features || []).map((feature, index) => (
                    <div key={index} className="flex items-center justify-between p-2 border rounded">
                      <span>{feature}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFeature(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('products.subscriptionPlanEditor.noFeatures')}</p>
              )}
            </div>

            {/* Feature Flags */}
            {availableFeatureFlags && availableFeatureFlags.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-normal">{t('products.subscriptionPlanEditor.featureFlagsTitle')}</CardTitle>
                  <CardDescription>{t('products.subscriptionPlanEditor.featureFlagsDescription')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {availableFeatureFlags.map((flag) => (
                      <div key={flag.key} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id={flag.key}
                            checked={formData.featureFlags?.[flag.key] || false}
                            onCheckedChange={(checked) => {
                              console.log(`Feature flag checkbox changed - ${flag.key}:`, checked);
                              const newFeatureFlags = { 
                                ...(formData.featureFlags || {}), 
                                [flag.key]: checked === true 
                              };
                              console.log('New feature flags state:', newFeatureFlags);
                              setFormData({ 
                                ...formData, 
                                featureFlags: newFeatureFlags
                              });
                            }}
                          />
                          <Label 
                            htmlFor={flag.key} 
                            className="font-normal cursor-pointer flex-1"
                          >
                            {flag.name || flag.key}
                          </Label>
                        </div>
                        <span className="text-sm text-muted-foreground font-mono">{flag.key}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Additional Settings */}
          <div className="space-y-4">
            <h3 className="text-base font-medium">{t('products.subscriptionPlanEditor.additionalSettingsTitle')}</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="visible-to-customers"
                  checked={formData.isAvailableInCheckout ?? true}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isAvailableInCheckout: checked })
                  }
                />
                <Label htmlFor="visible-to-customers" className="text-base font-normal cursor-pointer">
                  {t('products.subscriptionPlanEditor.visibleToCustomersLabel', 'Visible to customers')}
                </Label>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="recommended"
                  checked={formData.isRecommended}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, isRecommended: checked })
                  }
                />
                <Label htmlFor="recommended" className="text-base font-normal cursor-pointer">
                  {t('products.subscriptionPlanEditor.recommendedLabel')}
                </Label>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch
                    id="offer-trial"
                    checked={(formData.freeTrialDays || 0) > 0}
                    onCheckedChange={(checked) => 
                      setFormData({
                        ...formData,
                        freeTrialDays: checked ? 14 : 0,
                        trialRequiresCard: checked ? formData.trialRequiresCard ?? false : false,
                      })
                    }
                  />
                  <Label htmlFor="offer-trial" className="text-base font-normal cursor-pointer">
                    {t('products.subscriptionPlanEditor.freeTrialLabel')}
                  </Label>
                </div>

                {(formData.freeTrialDays || 0) > 0 && (
                  <div className="ml-12 space-y-2">
                    <Label htmlFor="trial-days">{t('products.subscriptionPlanEditor.freeTrialDaysLabel')}</Label>
                    <Input
                      id="trial-days"
                      type="number"
                      min="1"
                      max="90"
                      value={formData.freeTrialDays || 0}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        freeTrialDays: parseInt(e.target.value) || 0,
                        trialRequiresCard:
                          (parseInt(e.target.value) || 0) > 0
                            ? formData.trialRequiresCard ?? false
                            : false,
                      })}
                      className="w-32"
                    />
                    <div className="flex items-center gap-3 pt-2">
                      <Switch
                        id="trial-requires-card"
                        checked={formData.trialRequiresCard ?? false}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, trialRequiresCard: checked })
                        }
                      />
                      <Label htmlFor="trial-requires-card" className="text-sm font-normal cursor-pointer">
                        {t('products.subscriptionPlanEditor.trialRequiresCardLabel')}
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('products.subscriptionPlanEditor.trialBeforeIntro')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('products.subscriptionPlanEditor.saveButton')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Translation Dialogs */}
      <TranslationDialog
        open={showTranslationDialog}
        onOpenChange={setShowTranslationDialog}
        value={formData.name}
        translations={nameTranslations}
        onChange={setNameTranslations}
        fieldType="input"
        label={t('products.subscriptionPlanEditor.translationNameLabel')}
        supportedLanguages={supportedLanguages}
      />

      <TranslationDialog
        open={showDescriptionTranslationDialog}
        onOpenChange={setShowDescriptionTranslationDialog}
        value={formData.description || ''}
        translations={descriptionTranslations}
        onChange={setDescriptionTranslations}
        fieldType="textarea"
        label={t('products.subscriptionPlanEditor.translationDescriptionLabel')}
        supportedLanguages={supportedLanguages}
      />

      <FeaturesTranslationDialog
        open={showFeaturesTranslationDialog}
        onOpenChange={setShowFeaturesTranslationDialog}
        onChange={setFeaturesTranslations}
        features={formData.features?.features || []}
        translations={featuresTranslations}
        supportedLanguages={supportedLanguages}
      />
    </Dialog>
  );
}
